<script setup lang="ts">
import DSContainer from '~/components/ds/Container.vue'
import { useBlog, type BlogArticle, type BlogArticleListItem } from '~/composables/useBlog'
import { SITE_URL, serializeJsonLd } from '~/utils/schemaOrg'
import { HREFLANG_MAP } from '~/composables/useSeoCanonical'

const { t, locale } = useI18n()
const route = useRoute()
const localePath = useLocalePath()
const { fetchArticle, fetchRelated } = useBlog()

const slug = computed(() => route.params.slug as string)

const { data: article, error } = await useAsyncData<BlogArticle>(
  () => `blog:${locale.value}:${slug.value}`,
  () => fetchArticle(slug.value),
  { watch: [slug, locale] },
)

if (error.value || !article.value) {
  throw createError({ statusCode: 404, statusMessage: 'Article not found' })
}

const { data: related } = await useAsyncData<BlogArticleListItem[]>(
  () => `blog-related:${locale.value}:${article.value?.id || 'none'}`,
  () => fetchRelated(article.value!.id, 3),
  { watch: [article, locale] },
)

const canonical = computed(() =>
  article.value!.canonicalUrl || `${SITE_URL}${localePath(`/blog/${article.value!.slug}`)}`
)
const alternateLinks = computed(() => {
  if (article.value!.canonicalUrl) return []
  const path = `/blog/${article.value!.slug}`
  const links = Object.entries(HREFLANG_MAP).map(([code, hreflang]) => ({
    rel: 'alternate' as const,
    hreflang,
    href: `${SITE_URL}${code === 'es' ? path : `/${code}${path}`}`,
  }))
  links.push({ rel: 'alternate' as const, hreflang: 'x-default', href: `${SITE_URL}${path}` })
  return links
})

const ogImage = computed(() => article.value!.ogImageUrl || article.value!.coverImageUrl)
const authorProfileHref = computed(() => {
  const url = article.value!.authorProfileUrl
  if (!url || /^https:\/\//i.test(url)) return url
  return localePath(url)
})
const authorProfileSchemaUrl = computed(() => {
  const url = authorProfileHref.value
  if (!url || /^https:\/\//i.test(url)) return url
  return `${SITE_URL}${url.startsWith('/') ? '' : '/'}${url}`
})

const jsonLd = computed(() => ({
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline: article.value!.title,
  description: article.value!.metaDescription || article.value!.excerpt,
  image: ogImage.value ? [ogImage.value] : undefined,
  datePublished: article.value!.publishedAt,
  dateModified: article.value!.updatedAt || article.value!.publishedAt,
  author: {
    '@type': article.value!.authorName.trim().toLowerCase() === 'midigi team' ? 'Organization' : 'Person',
    name: article.value!.authorName,
    url: authorProfileSchemaUrl.value || SITE_URL,
  },
  ...(article.value!.reviewedBy ? { reviewedBy: { '@type': 'Person', name: article.value!.reviewedBy } } : {}),
  ...(article.value!.sourceUrls?.length ? { citation: article.value!.sourceUrls } : {}),
  publisher: {
    '@type': 'Organization',
    name: 'MiDiGi',
    logo: { '@type': 'ImageObject', url: `${SITE_URL}/favicon.svg` },
  },
  mainEntityOfPage: { '@type': 'WebPage', '@id': canonical.value },
  inLanguage: locale.value,
  ...(article.value!.category ? { articleSection: article.value!.category.name } : {}),
  ...(article.value!.tags?.length ? { keywords: article.value!.tags.map(t => t.name).join(', ') } : {}),
}))

useHead(() => ({
  title: article.value!.metaTitle || article.value!.title,
  meta: [
    { name: 'description', content: article.value!.metaDescription || article.value!.excerpt || '' },
    { property: 'og:type', content: 'article' },
    { property: 'og:title', content: article.value!.metaTitle || article.value!.title },
    { property: 'og:description', content: article.value!.metaDescription || article.value!.excerpt || '' },
    { property: 'og:url', content: canonical.value },
    ...(ogImage.value ? [{ property: 'og:image', content: ogImage.value }] : []),
    ...(article.value!.publishedAt ? [{ property: 'article:published_time', content: article.value!.publishedAt }] : []),
    ...(article.value!.updatedAt ? [{ property: 'article:modified_time', content: article.value!.updatedAt }] : []),
    ...(article.value!.authorName ? [{ property: 'article:author', content: article.value!.authorName }] : []),
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:title', content: article.value!.metaTitle || article.value!.title },
    { name: 'twitter:description', content: article.value!.metaDescription || article.value!.excerpt || '' },
    ...(ogImage.value ? [{ name: 'twitter:image', content: ogImage.value }] : []),
  ],
  link: [{ rel: 'canonical', href: canonical.value }, ...alternateLinks.value],
  script: [
    {
      type: 'application/ld+json',
      innerHTML: serializeJsonLd(jsonLd.value),
    },
  ],
}))

function formatDate(d?: string) {
  if (!d) return ''
  return new Date(d).toLocaleDateString()
}
</script>

<template>
  <DSContainer v-if="article" class="py-8">
    <article>
      <nav class="max-w-4xl text-sm text-gray-500 mb-6">
        <NuxtLink :to="localePath('/blog')" class="hover:text-primary-600">{{ t('blog.pageTitle') }}</NuxtLink>
        <span v-if="article.category"> / </span>
        <NuxtLink
          v-if="article.category"
          :to="localePath(`/blog/category/${article.category.slug}`)"
          class="hover:text-primary-600"
        >
          {{ article.category.name }}
        </NuxtLink>
      </nav>

      <header class="mb-8">
        <h1 class="text-4xl font-bold leading-tight">{{ article.title }}</h1>
        <p v-if="article.excerpt" class="mt-4 text-xl text-gray-600 dark:text-gray-400">{{ article.excerpt }}</p>
        <div class="mt-6 flex items-center gap-3 text-sm text-gray-500">
          <a
            v-if="article.authorProfileUrl"
            :href="authorProfileHref"
            class="hover:text-primary-600 hover:underline"
          >{{ article.authorName }}</a>
          <span v-else>{{ article.authorName }}</span>
          <span>·</span>
          <time :datetime="article.publishedAt">{{ formatDate(article.publishedAt) }}</time>
          <span v-if="article.readingMinutes">·</span>
          <span v-if="article.readingMinutes">{{ t('blog.minutesRead', { n: article.readingMinutes }) }}</span>
        </div>
      </header>

      <div class="max-w-4xl mx-auto">
        <NuxtImg
          v-if="article.coverImageUrl"
          :src="article.coverImageUrl"
          :alt="article.title"
          class="w-full aspect-video object-cover rounded-xl mb-10"
          width="1200"
          height="675"
          loading="eager"
          fetchpriority="high"
        />

        <RichContentRenderer class="prose-lg" :content="article.content" />

        <aside class="mt-10 rounded-xl border border-gray-200 bg-gray-50 p-5 dark:border-gray-800 dark:bg-gray-900">
          <div class="flex flex-wrap items-center gap-2 text-sm">
            <span class="font-semibold text-gray-900 dark:text-white">{{ t('blog.factStatusLabel') }}:</span>
            <span class="rounded-full bg-white px-2.5 py-1 dark:bg-gray-800">
              {{ t(`blog.factStatuses.${article.factStatus || 'unreviewed'}`) }}
            </span>
            <span v-if="article.reviewedBy" class="text-gray-600 dark:text-gray-400">
              {{ t('blog.reviewedBy', { name: article.reviewedBy }) }}
            </span>
          </div>
          <div v-if="article.sourceUrls?.length" class="mt-4">
            <h2 class="text-base font-semibold text-gray-900 dark:text-white">{{ t('blog.sourcesTitle') }}</h2>
            <ol class="mt-2 list-decimal space-y-1 pl-5 text-sm">
              <li v-for="source in article.sourceUrls" :key="source">
                <a :href="source" target="_blank" rel="noopener noreferrer" class="break-all text-primary-600 hover:underline">{{ source }}</a>
              </li>
            </ol>
          </div>
        </aside>
      </div>

      <footer v-if="article.tags?.length" class="mt-10 pt-6 border-t border-gray-200 dark:border-gray-800 flex flex-wrap gap-2">
        <NuxtLink
          v-for="tag in article.tags"
          :key="tag.slug"
          :to="localePath(`/blog/tag/${tag.slug}`)"
          class="text-sm px-3 py-1 rounded-full bg-gray-100 dark:bg-gray-800 hover:bg-primary-100 dark:hover:bg-primary-900/30"
        >
          #{{ tag.name }}
        </NuxtLink>
      </footer>

      <nav class="mt-8 flex flex-wrap gap-3 text-sm" :aria-label="t('blog.exploreMore')">
        <NuxtLink :to="localePath('/shop')" class="text-primary-600 hover:underline">{{ t('nav.shop') }}</NuxtLink>
        <NuxtLink :to="localePath('/compramos')" class="text-primary-600 hover:underline">{{ t('nav.weBuy') }}</NuxtLink>
        <NuxtLink :to="localePath('/restauracion')" class="text-primary-600 hover:underline">{{ t('services.restoration') }}</NuxtLink>
      </nav>

      <section v-if="related?.length" class="mt-16">
        <h2 class="text-2xl font-bold mb-6">{{ t('blog.relatedTitle') }}</h2>
        <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
          <NuxtLink
            v-for="r in related"
            :key="r.id"
            :to="localePath(`/blog/${r.slug}`)"
            class="group"
          >
            <div class="aspect-video rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-800 mb-3">
              <NuxtImg
                v-if="r.coverImageUrl"
                :src="r.coverImageUrl"
                :alt="r.title"
                class="w-full h-full object-cover group-hover:scale-105 transition-transform"
                width="400"
                height="225"
                loading="lazy"
              />
            </div>
            <h3 class="font-semibold leading-tight group-hover:text-primary-600 dark:group-hover:text-primary-400">
              {{ r.title }}
            </h3>
            <p v-if="r.excerpt" class="mt-1 text-sm text-gray-500 line-clamp-2">{{ r.excerpt }}</p>
          </NuxtLink>
        </div>
      </section>
    </article>
  </DSContainer>
</template>
